import React, { useState, useCallback, useRef, useEffect, useImperativeHandle } from 'react';
import { useSpellCheck, SpellCheckOptions } from '../utils/spellchecker-browser';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { AlertCircle, CheckCircle2, User as UserIcon } from 'lucide-react';

export interface MentionParticipant {
  id: string;
  nome: string;
}

interface SpellCheckInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  type?: 'input' | 'textarea';
  rows?: number;
  showSuggestions?: boolean;
  autoCorrect?: boolean;
  options?: SpellCheckOptions;
  disabled?: boolean;
  maxLength?: number;
  onPaste?: (e: React.ClipboardEvent) => void;
  onInput?: (e: React.FormEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  autoFocus?: boolean;
  mentionsList?: MentionParticipant[];
  onMentionsChange?: (mentions: MentionParticipant[]) => void;
}

interface Suggestion {
  word: string;
  suggestions: string[];
  position: number;
}

export const SpellCheckInput = React.forwardRef<HTMLTextAreaElement | HTMLInputElement, SpellCheckInputProps>(({
  value = '',
  onChange,
  placeholder,
  className,
  type = 'textarea',
  rows = 3,
  showSuggestions = true,
  autoCorrect = false,
  options,
  disabled = false,
  maxLength,
  onPaste,
  onInput,
  onKeyDown,
  onFocus,
  onBlur,
  onClick,
  autoFocus,
  mentionsList = [],
  onMentionsChange
}, ref) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSpellCheckMenu, setShowSpellCheckMenu] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  
  // Mentions state
  const [showMentionsMenu, setShowMentionsMenu] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionIndex, setMentionIndex] = useState(-1);
  const [selectedMentions, setSelectedMentions] = useState<MentionParticipant[]>([]);
  // Map de número → nome para exibição após inserção
  const [mentionDisplayMap, setMentionDisplayMap] = useState<Record<string, string>>({});

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const inputRefForInput = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mentionsMenuRef = useRef<HTMLDivElement>(null);
  const spellCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { checkText, autoCorrect: autoCorrectText } = useSpellCheck(options);

  // Expor a ref interna para uso externo
  useImperativeHandle(ref, () => {
    const currentRef = type === 'textarea' ? inputRef.current : inputRefForInput.current;
    return currentRef as HTMLTextAreaElement | HTMLInputElement;
  }, [type]);

  // Verificação de ortografia
  const checkSpelling = useCallback((text: string) => {
    if (!text || text.trim().length === 0) {
      setSuggestions([]);
      return;
    }

    setIsChecking(true);
    const textErrors = checkText(text);

    const suggestionList: Suggestion[] = textErrors.map(error => ({
      word: error.word,
      suggestions: error.suggestions || [],
      position: text.indexOf(error.word)
    }));

    setSuggestions(suggestionList);
    setIsChecking(false);
  }, [checkText]);

  // Auto-correção
  const handleAutoCorrect = useCallback(() => {
    if (autoCorrect && value) {
      const corrected = autoCorrectText(value);
      if (corrected !== value) {
        onChange(corrected);
      }
    }
  }, [autoCorrect, value, onChange, autoCorrectText]);

  // Aplicar sugestão
  const applySuggestion = useCallback((suggestion: Suggestion, replacement: string) => {
    const newValue = value.replace(suggestion.word, replacement);
    onChange(newValue);
    setShowSpellCheckMenu(false);

    // Foca no input novamente
    setTimeout(() => {
      if (type === 'textarea' && inputRef.current) {
        inputRef.current.focus();
      } else if (type === 'input' && inputRefForInput.current) {
        inputRefForInput.current.focus();
      }
    }, 0);
  }, [value, onChange]);

  // Lidar com mudanças no texto
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Clear previous timeout
    if (spellCheckTimeoutRef.current) {
      clearTimeout(spellCheckTimeoutRef.current);
    }

    // Verificação em tempo real (com debounce)
    spellCheckTimeoutRef.current = setTimeout(() => {
      checkSpelling(newValue);
    }, 500);

    // Lógica de menção
    if (mentionsList.length > 0) {
      const currentRef = type === 'textarea' ? inputRef.current : inputRefForInput.current;
      if (currentRef) {
        const cursorPosition = currentRef.selectionStart || 0;
        const textBeforeCursor = newValue.substring(0, cursorPosition);
        
        // Check if there is an '@' before the cursor without a space after it
        const match = textBeforeCursor.match(/@([a-zA-Z0-9_]*)$/);
        
        if (match) {
          setShowMentionsMenu(true);
          setMentionQuery(match[1].toLowerCase());
          setMentionIndex(textBeforeCursor.lastIndexOf('@'));
        } else {
          setShowMentionsMenu(false);
        }
      }
    }
  }, [onChange, checkSpelling, mentionsList, type]);

  // Aplicar menção
  const applyMention = useCallback((participant: MentionParticipant) => {
    if (mentionIndex !== -1) {
      const beforeMention = value.substring(0, mentionIndex);
      const afterMention = value.substring(mentionIndex + mentionQuery.length + 1); // +1 for the '@'
      
      // Extrair só o número do id (remove @s.whatsapp.net, @g.us, @lid)
      const phoneNumber = participant.id
        .replace('@s.whatsapp.net', '')
        .replace('@g.us', '')
        .replace('@lid', '');
      
      // O texto precisa conter @numero para o WhatsApp criar a menção real
      const newMentionText = `@${phoneNumber} `;
      const newValue = `${beforeMention}${newMentionText}${afterMention}`;
      
      onChange(newValue);
      
      // Atualizar mapa de exibição número → nome
      const newDisplayMap = { ...mentionDisplayMap, [phoneNumber]: participant.nome };
      setMentionDisplayMap(newDisplayMap);
      
      const newSelectedMentions = [...selectedMentions, participant];
      setSelectedMentions(newSelectedMentions);
      if (onMentionsChange) {
        onMentionsChange(newSelectedMentions);
      }

      setShowMentionsMenu(false);
      
      // Reposicionar cursor
      setTimeout(() => {
        const currentRef = type === 'textarea' ? inputRef.current : inputRefForInput.current;
        if (currentRef) {
          currentRef.focus();
          const newPos = mentionIndex + newMentionText.length;
          currentRef.setSelectionRange(newPos, newPos);
        }
      }, 0);
    }
  }, [value, mentionIndex, mentionQuery, onChange, type, selectedMentions, onMentionsChange, mentionDisplayMap]);

  // Adicionar callback externo para mudanças imediatas (para rascunho)
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChange(e);
    // Chamar onInput externo se fornecido (para salvamento de rascunho)
    if (onInput) {
      onInput(e);
    }
  }, [handleChange, onInput]);

  // Lidar com teclas de atalho
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Chama o handler externo primeiro
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (showSpellCheckMenu && suggestions.length > 0) {
      // Fechar menu com Escape
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSpellCheckMenu(false);
      }
    }

    // Ctrl/Cmd + Enter para auto-corrigir
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleAutoCorrect();
    }
    
    // Controles para menu de menção
    if (showMentionsMenu && mentionsList.length > 0) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionsMenu(false);
      } else if (e.key === 'Enter') {
        const filteredList = mentionsList.filter(p => p.nome.toLowerCase().includes(mentionQuery) || p.id.toLowerCase().includes(mentionQuery));
        if (filteredList.length > 0) {
          e.preventDefault();
          applyMention(filteredList[0]);
        }
      }
    }
  }, [showSpellCheckMenu, suggestions, handleAutoCorrect, onKeyDown, showMentionsMenu, mentionsList, mentionQuery, applyMention]);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowSpellCheckMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup timeout on unmount
      if (spellCheckTimeoutRef.current) {
        clearTimeout(spellCheckTimeoutRef.current);
      }
    };
  }, []);

  // Fechar menu de menções ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mentionsMenuRef.current && !mentionsMenuRef.current.contains(event.target as Node)) {
        setShowMentionsMenu(false);
      }
    };

    if (showMentionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMentionsMenu]);

  // Se o valor for limpo, limpe as menções selecionadas
  useEffect(() => {
    if (value === '' && selectedMentions.length > 0) {
      setSelectedMentions([]);
      setMentionDisplayMap({});
      if (onMentionsChange) {
        onMentionsChange([]);
      }
    }
  }, [value, selectedMentions, onMentionsChange]);

  // Verificação inicial
  useEffect(() => {
    checkSpelling(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, checkText]);

  const hasErrors = suggestions.length > 0;
  const showIndicator = showSuggestions && hasErrors && !disabled;

  const InputComponent = type === 'input' ? Input : Textarea;
  const currentRef = type === 'input' ? inputRefForInput : inputRef;

  return (
    <div className="relative">
      <div className="relative">
        <InputComponent
          ref={currentRef as any}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={onClick}
          onPaste={onPaste}
          onInput={onInput}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          rows={type === 'textarea' ? rows : undefined}
          autoFocus={autoFocus}
          className={cn(
            'pr-10',
            showIndicator && 'border-orange-300 focus:border-orange-400',
            className
          )}
        />

        {/* Indicador de spellcheck */}
        {showIndicator && (
          <div className="absolute right-2 top-2">
            {isChecking ? (
              <div className="animate-spin h-4 w-4 border-2 border-orange-300 border-t-transparent rounded-full" />
            ) : (
              <button
                type="button"
                onClick={() => setShowSpellCheckMenu(!showSpellCheckMenu)}
                className="text-orange-500 hover:text-orange-600 transition-colors"
                title={`${suggestions.length} erro(s) de ortografia`}
              >
                <AlertCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Menu de sugestões */}
      {showSpellCheckMenu && suggestions.length > 0 && (
        <div
          ref={menuRef}
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] md:w-[500px] mb-2 transform bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Sugestões de ortografia
            </h4>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <div key={index} className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    <span className="font-medium text-sm text-red-600 dark:text-red-400">
                      {suggestion.word}
                    </span>
                  </div>
                  {index === 0 && autoCorrect && (
                    <button
                      type="button"
                      onClick={handleAutoCorrect}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Auto-corrigir tudo
                    </button>
                  )}
                </div>

                {suggestion.suggestions.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {suggestion.suggestions.map((replacement, repIndex) => (
                      <button
                        key={repIndex}
                        type="button"
                        onClick={() => applySuggestion(suggestion, replacement)}
                        className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 dark:bg-slate-700 hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-900 dark:hover:text-blue-300"
                      >
                        {replacement}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 italic">Nenhuma sugestão disponível</p>
                )}
              </div>
            ))}
          </div>


        </div>
      )}

      {/* Menu de menções */}
      {showMentionsMenu && mentionsList.length > 0 && (
        <div
          ref={mentionsMenuRef}
          className="absolute z-[60] bottom-full left-0 w-64 mb-2 transform bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Mencionar membro
            </h4>
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {mentionsList.filter(p => p.nome.toLowerCase().includes(mentionQuery) || p.id.toLowerCase().includes(mentionQuery)).length > 0 ? (
              mentionsList
                .filter(p => p.nome.toLowerCase().includes(mentionQuery) || p.id.toLowerCase().includes(mentionQuery))
                .map((participant, index) => (
                  <button
                    key={participant.id}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-left"
                    onClick={(e) => {
                      e.preventDefault();
                      applyMention(participant);
                    }}
                  >
                    <div className="h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <UserIcon className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="truncate">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{participant.nome}</p>
                      <p className="text-[10px] text-gray-500 truncate">{participant.id.split('@')[0]}</p>
                    </div>
                  </button>
                ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 italic text-center">Nenhum membro encontrado</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

SpellCheckInput.displayName = 'SpellCheckInput';

// Componente simplificado para apenas indicar erros
export const SpellCheckIndicator: React.FC<{
  text: string;
  className?: string;
}> = ({ text, className }) => {
  const { checkText } = useSpellCheck();
  const [errorCount, setErrorCount] = useState(0);

  useEffect(() => {
    const errors = checkText(text);
    setErrorCount(errors.length);
  }, [text, checkText]);

  if (errorCount === 0) {
    return (
      <div className={cn("flex items-center gap-1 text-green-600", className)}>
        <CheckCircle2 className="h-3 w-3" />
        <span className="text-xs">Sem erros</span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-1 text-orange-600", className)}>
      <AlertCircle className="h-3 w-3" />
      <span className="text-xs">{errorCount} erro(s)</span>
    </div>
  );
};

export default SpellCheckInput;
